import { useMemo } from 'react'
import { useQueries } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import {
    buildDetailedBrandRows,
    getBrandColor,
    type DetailedBrandComparisonRow,
    type DetailedBrandOption,
} from '../lib/detailedBrandComparison'
import type { CompanyStatsComparisonMode } from './useCompanyStatsBrandComparison'
import { companyStatsDataFetchers } from './index'
import type { CompanyStatsDataFetchers } from './hooks'
import type { GenerationScoreStatsApiV1StatsOrgIdScoresGetResponse } from '@/common/api/client/types.gen'

export type UseCompanyStatsDetailedBrandComparisonResult = {
    brandOptions: DetailedBrandOption[]
    rows: DetailedBrandComparisonRow[]
    isCommonLoading: boolean
    isScoresLoading: boolean
    isLoading: boolean
    error: AxiosError | null
    refetch: () => void
}

export const useCompanyStatsDetailedBrandComparison = (
    orgId: string | undefined,
    compareBy: CompanyStatsComparisonMode,
    allBrands: Array<{ id: string; name: string }>,
    selectedBrandIds: string[],
    fetchers: CompanyStatsDataFetchers = companyStatsDataFetchers
): UseCompanyStatsDetailedBrandComparisonResult => {
    const enabled = Boolean(orgId) && allBrands.length > 0
    const buildFilter = (entityId: string) => (compareBy === 'brand' ? { brand: entityId } : { seller: entityId })

    const colorById = useMemo(() => {
        const map = new Map<string, string>()
        allBrands.forEach((brand, index) => {
            map.set(brand.id, getBrandColor(index))
        })
        return map
    }, [allBrands])

    const commonQueries = useQueries({
        queries: allBrands.map(brand => ({
            queryKey: ['company-stats', 'common', compareBy, orgId, brand.id],
            queryFn: () => fetchers.fetchCommon(orgId!, buildFilter(brand.id)),
            retry: false,
            enabled,
            refetchOnWindowFocus: false,
        })),
    })

    const selectedBrands = useMemo(
        () => allBrands.filter(brand => selectedBrandIds.includes(brand.id)),
        [allBrands, selectedBrandIds]
    )

    const scoresQueries = useQueries({
        queries: selectedBrands.map(brand => ({
            queryKey: ['company-stats', 'scores', compareBy, orgId, brand.id],
            queryFn: () => fetchers.fetchScores(orgId!, buildFilter(brand.id)),
            retry: false,
            enabled: enabled && selectedBrandIds.length > 0,
            refetchOnWindowFocus: false,
        })),
    })

    const brandOptions = useMemo((): DetailedBrandOption[] => {
        return allBrands.map((brand, index) => {
            const commonData = commonQueries[index]?.data
            return {
                id: brand.id,
                name: brand.name,
                productCount: commonData?.total_products ?? 0,
                color: colorById.get(brand.id) ?? getBrandColor(index),
            }
        })
    }, [allBrands, colorById, commonQueries])

    const rows = useMemo((): DetailedBrandComparisonRow[] => {
        const commonById = new Map<string, { total_products: number; avg_score?: number }>()
        allBrands.forEach((brand, index) => {
            const data = commonQueries[index]?.data
            if (data) {
                commonById.set(brand.id, {
                    total_products: data.total_products,
                    avg_score: data.avg_score,
                })
            }
        })

        const scoresById = new Map<
            string,
            {
                avg_score: number
                max_score: number
                blocks: GenerationScoreStatsApiV1StatsOrgIdScoresGetResponse['blocks']
            }
        >()
        selectedBrands.forEach((brand, index) => {
            const data = scoresQueries[index]?.data
            if (data) {
                scoresById.set(brand.id, data)
            }
        })

        return buildDetailedBrandRows(selectedBrands, commonById, scoresById, colorById)
    }, [allBrands, colorById, commonQueries, scoresQueries, selectedBrands])

    const isCommonLoading = commonQueries.some(query => query.isLoading)
    const isScoresLoading = selectedBrandIds.length > 0 && scoresQueries.some(query => query.isLoading)
    const isLoading = isCommonLoading || isScoresLoading

    const error =
        (commonQueries.find(query => query.error)?.error as AxiosError | undefined) ??
        (scoresQueries.find(query => query.error)?.error as AxiosError | undefined) ??
        null

    const refetch = () => {
        commonQueries.forEach(query => void query.refetch())
        scoresQueries.forEach(query => void query.refetch())
    }

    return {
        brandOptions,
        rows,
        isCommonLoading,
        isScoresLoading,
        isLoading,
        error,
        refetch,
    }
}
